/**
 * @file 学びメモの一覧の並べ替えのボタンとメニューである。
 *
 * @what: 印だけのボタンを押すと、並びの候補 (model/noteList.ts の NOTE_SORTS) を小さなメニューで出し、
 *   いまの並びに印を付ける。選ぶか、外を押すか、Esc でメニューを閉じる。
 * @why: 一覧の並びを選べるようにする。ボタンは文字を減らすため印だけにする (代表の判断、2026-11-02)。
 * @failure: 描画のみ。選んだ並びを覚えるのは呼ぶ側である。
 */

import { useEffect, useId, useRef, useState } from "react";
import { NOTE_SORTS, type NoteSort } from "../model/noteList";
import "./study.css";

/** 並べ替えの印 (長さの違う3本の線と下向きの矢印) である。 */
function SortIcon() {
  return (
    <svg
      className="study-notebook-icon"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 4h7M2 8h5M2 12h3" />
      <path d="M12 3v10M9.5 10.5 12 13l2.5-2.5" />
    </svg>
  );
}

export function NoteSortMenu({
  sort,
  onChange,
}: {
  sort: NoteSort;
  onChange: (sort: NoteSort) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const menuId = useId();
  const current = NOTE_SORTS.find((s) => s.sort === sort)?.label ?? "";

  // 外を押したら閉じる
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <span
      ref={rootRef}
      className="study-notebook-sort"
      onKeyDown={(e) => {
        // メニューの Esc は全画面の Esc に届かせず、メニューだけを閉じる
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className="study-notebook-icon-btn"
        aria-label={`並べ替え (${current})`}
        title={`並べ替え (${current})`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <SortIcon />
      </button>
      {open ? (
        <ul id={menuId} className="study-notebook-sort-menu" role="menu" aria-label="並べ替え">
          {NOTE_SORTS.map((s) => (
            <li key={s.sort} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={s.sort === sort}
                className="study-notebook-sort-item"
                onClick={() => {
                  onChange(s.sort);
                  setOpen(false);
                }}
              >
                <span className="study-notebook-sort-check" aria-hidden="true">
                  {s.sort === sort ? "✓" : ""}
                </span>
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}
